import { useState } from 'react';
import { ExternalLink } from 'lucide-react-native';
import { Image, Pressable, ScrollView, StyleSheet, View, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native';

import { Card } from '@/components/Card';
import { PageDots } from '@/components/PageDots';
import { Text } from '@/components/Text';
import { colors, fontSizes, radii, spacing } from '@/constants/theme';
import { formatMoney } from '@/lib/money';
import { openWebUrl } from '@/lib/openWebUrl';
import type { OrderedProduct, VoltrProduct } from '@/types/voltr';

export type SocialProductCardProps = {
  /** The brand's choices, or the single product the user ordered. */
  products: (VoltrProduct | OrderedProduct)[];
  testID?: string;
};

/**
 * The brand's products (from /social): photo, "FREE | $X VALUE",
 * name. One product is a full-width card (Figma): the one the user ordered,
 * once they have. Before that, several choices scroll sideways (Figma
 * "Multiple products") under "Choose 1 free product", so nobody reads the
 * row as everything they get, with dots for where the row is. "View
 * product" opens the brand's page when Voltr sends one.
 */
export function SocialProductCard({ products, testID }: SocialProductCardProps) {
  if (products.length === 1) {
    return <ProductTile product={products[0]!} testID={testID} />;
  }
  return <ProductChoices products={products} testID={testID} />;
}

function ProductChoices({ products, testID }: SocialProductCardProps) {
  const [page, setPage] = useState(0);
  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const next = Math.round(e.nativeEvent.contentOffset.x / (NARROW_WIDTH + spacing.sm));
    setPage(Math.min(Math.max(next, 0), products.length - 1));
  };
  return (
    <View>
      <View style={styles.header}>
        <Text variant="bodyBold">Choose 1 free product</Text>
        <Text variant="caption">You’ll pick your favorite when you order.</Text>
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        onScroll={onScroll}
        scrollEventThrottle={32}
        style={styles.scroller}
        contentContainerStyle={styles.row}
        testID={testID}
      >
        {products.map((product) => (
          <ProductTile key={product.id ?? product.title} product={product} narrow />
        ))}
      </ScrollView>
      <PageDots count={products.length} active={page} testID={testID ? `${testID}-dots` : undefined} />
    </View>
  );
}

type TileProps = { product: VoltrProduct | OrderedProduct; narrow?: boolean; testID?: string };

function ProductTile({ product, narrow, testID }: TileProps) {
  const url = product.product_url;
  return (
    <Card variant="flat" padding={0} style={[styles.tile, narrow && styles.narrow]} testID={testID}>
      <View style={styles.body}>
        {product.image_url ? (
          <Image source={{ uri: product.image_url }} style={styles.image} resizeMode="cover" />
        ) : (
          <View style={styles.image} />
        )}
        <View style={styles.text}>
          <Text variant="caption" color="textMuted" uppercase numberOfLines={2} style={styles.eyebrow}>
            {product.price_cents != null ? `Free | ${formatMoney(product.price_cents)} value` : 'Free'}
          </Text>
          <Text variant="bodyBold" color="deepGreen" numberOfLines={2} style={styles.title}>
            {product.title}
          </Text>
        </View>
      </View>
      {url ? (
        <Pressable
          onPress={() => openWebUrl(url)}
          accessibilityRole="link"
          accessibilityLabel={`View ${product.title}`}
          style={({ pressed }) => [styles.link, pressed && styles.pressed]}
          testID={testID ? `${testID}-link` : undefined}
        >
          <Text variant="bodyBold" color="deepGreen">
            View product
          </Text>
          <ExternalLink size={16} color={colors.deepGreen} />
        </Pressable>
      ) : null}
    </Card>
  );
}

const IMAGE = 56;
export const NARROW_WIDTH = 264;

const styles = StyleSheet.create({
  // Figma: 12 under the header; the dots sit right under the cards.
  header: {
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  // Bleeds to the screen edge so the row reads as scrollable.
  scroller: {
    marginHorizontal: -spacing.lg,
  },
  row: {
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
  },
  tile: {
    overflow: 'hidden',
  },
  // Grows so, in a row of choices, every View product lines up at the bottom.
  body: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.base,
  },
  // Figma "View product": a full-width row under a hairline, 44pt tall.
  link: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 44,
    paddingHorizontal: spacing.base,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  pressed: {
    opacity: 0.6,
  },
  narrow: {
    width: NARROW_WIDTH,
  },
  image: {
    width: IMAGE,
    height: IMAGE,
    borderRadius: radii.base,
    backgroundColor: colors.surfaceSunken,
  },
  text: {
    flex: 1,
    gap: 2,
  },
  eyebrow: {
    fontSize: fontSizes.xs,
    letterSpacing: 0.4,
  },
  title: {
    fontSize: fontSizes.lg,
    lineHeight: 24,
  },
});
